import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Textarea } from './ui/textarea';
import { 
  Mic, 
  Square, 
  Upload, 
  Play, 
  Pause, 
  Trash2, 
  Check, 
  Loader2,
  Volume2,
  Edit3,
  X
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function VoiceNoteRecorderV2({ onSave, existingNotes = [], isOpen, onClose }) {
  // Simplified state - one state variable for the entire workflow
  const [step, setStep] = useState('choice'); // choice, recording, transcribing, editing
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [transcription, setTranscription] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState('');
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioPlayerRef = useRef(null);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, [audioUrl]);

  // Reset when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      handleReset();
    } else {
      setStep('choice');
    }
  }, [isOpen]);

  const handleReset = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    
    setStep('choice');
    setIsRecording(false);
    setAudioBlob(null);
    setAudioUrl(null);
    setTranscription('');
    setIsTranscribing(false);
    setIsPlaying(false);
    setRecordingTime(0);
    setError('');
  };

  const startRecording = async () => {
    try {
      setError('');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Use webm for better browser compatibility, or wav
        const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/wav';
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(blob);
        setAudioBlob(blob);
        setAudioUrl(url);
        stream.getTracks().forEach(track => track.stop());
        setIsRecording(false);
        
        // Automatically transcribe
        transcribeAudio(blob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setStep('recording');
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (err) {
      console.error('Error accessing microphone:', err);
      setError('Could not access microphone. Please grant permission and try again.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('audio/')) {
      setError('Please upload an audio file (MP3, WAV, M4A, etc.)');
      return;
    }

    setError('');
    const url = URL.createObjectURL(file);
    setAudioBlob(file);
    setAudioUrl(url);
    
    // Automatically transcribe
    transcribeAudio(file);
  };

  const transcribeAudio = async (blob) => {
    setStep('transcribing');
    setIsTranscribing(true);
    setError('');
    
    try {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      
      reader.onloadend = async () => {
        try {
          const base64Audio = reader.result.split(',')[1];
          const token = localStorage.getItem('auth_token');
          
          // Determine audio format
          let audioFormat = 'wav';
          if (blob.type) {
            const formatPart = blob.type.split('/')[1];
            // Handle common formats
            if (formatPart.includes('webm')) {
              audioFormat = 'webm';
            } else if (formatPart.includes('mp4') || formatPart.includes('m4a')) {
              audioFormat = 'mp4';
            } else if (formatPart.includes('mpeg') || formatPart.includes('mp3')) {
              audioFormat = 'mp3';
            } else {
              audioFormat = formatPart.split(';')[0]; // Remove any additional params
            }
          }
          
          const response = await axios.post(
            `${BACKEND_URL}/api/ai/transcribe-audio`,
            {
              audio_data: base64Audio,
              audio_format: audioFormat
            },
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );

          setTranscription(response.data.transcription);
          setStep('editing');
        } catch (err) {
          console.error('Transcription error:', err);
          setError('Failed to transcribe audio: ' + (err.response?.data?.detail || err.message));
          setStep('choice');
        } finally {
          setIsTranscribing(false);
        }
      };
      
      reader.onerror = () => {
        setError('Failed to read audio file');
        setIsTranscribing(false);
        setStep('choice');
      };
    } catch (err) {
      console.error('Transcription error:', err);
      setError('Failed to transcribe audio: ' + err.message);
      setIsTranscribing(false);
      setStep('choice');
    }
  };

  const playAudio = () => {
    if (audioPlayerRef.current) {
      if (isPlaying) {
        audioPlayerRef.current.pause();
      } else {
        audioPlayerRef.current.play();
      }
    }
  };

  const handleSave = () => {
    if (!transcription.trim()) {
      setError('Please add a transcription');
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(audioBlob);
    
    reader.onloadend = () => {
      const voiceNote = {
        id: Date.now().toString(),
        audio_data: reader.result,
        transcription: transcription.trim(),
        duration_seconds: recordingTime || null,
        created_at: new Date().toISOString()
      };

      onSave(voiceNote);
      handleReset();
      onClose();
    };
    
    reader.onerror = () => {
      setError('Failed to save voice note');
    };
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={(e) => {
      if (e.target === e.currentTarget) {
        handleReset();
        onClose();
      }
    }}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Volume2 className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-semibold">Add Voice Note</h2>
            </div>
            <button 
              onClick={() => {
                handleReset();
                onClose();
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Record or upload audio to add transcribed notes
          </p>

        <div className="space-y-4">
          {/* Error Display */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Choice Screen */}
          {step === 'choice' && (
            <div className="grid grid-cols-2 gap-4">
              <Button
                variant="outline"
                className="h-32 flex flex-col items-center justify-center gap-3 hover:border-red-500 hover:bg-red-50"
                onClick={startRecording}
              >
                <Mic className="h-12 w-12 text-red-500" />
                <div>
                  <div className="font-semibold">Record Audio</div>
                  <div className="text-xs text-gray-500">Use your microphone</div>
                </div>
              </Button>

              <Button
                variant="outline"
                className="h-32 flex flex-col items-center justify-center gap-3 hover:border-blue-500 hover:bg-blue-50"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-12 w-12 text-blue-500" />
                <div>
                  <div className="font-semibold">Upload Audio</div>
                  <div className="text-xs text-gray-500">MP3, WAV, M4A, etc.</div>
                </div>
              </Button>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          )}

          {/* Recording Screen */}
          {step === 'recording' && (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full ${
                    isRecording ? 'bg-red-100 animate-pulse' : 'bg-gray-100'
                  }`}>
                    <Mic className={`h-12 w-12 ${isRecording ? 'text-red-600' : 'text-gray-400'}`} />
                  </div>

                  <div className="text-2xl font-bold text-red-600">
                    {formatTime(recordingTime)}
                  </div>

                  <div className="flex gap-2 justify-center">
                    {isRecording ? (
                      <Button onClick={stopRecording} variant="destructive">
                        <Square className="h-4 w-4 mr-2" />
                        Stop Recording
                      </Button>
                    ) : (
                      <Button onClick={startRecording} className="bg-red-600 hover:bg-red-700">
                        <Mic className="h-4 w-4 mr-2" />
                        Start Recording
                      </Button>
                    )}
                    <Button variant="outline" onClick={handleReset}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Transcribing Screen */}
          {step === 'transcribing' && (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center space-y-4 py-8">
                  <Loader2 className="h-16 w-16 mx-auto animate-spin text-blue-600" />
                  <div>
                    <div className="text-lg font-semibold">Transcribing audio...</div>
                    <div className="text-sm text-gray-500 mt-1">This may take a few moments</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Editing Screen */}
          {step === 'editing' && (
            <div className="space-y-4">
              {/* Audio Player */}
              {audioUrl && (
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={playAudio}
                        className="flex-shrink-0"
                      >
                        {isPlaying ? (
                          <Pause className="h-4 w-4" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                      </Button>
                      
                      <div className="flex-1">
                        <audio
                          ref={audioPlayerRef}
                          src={audioUrl}
                          onEnded={() => setIsPlaying(false)}
                          onPlay={() => setIsPlaying(true)}
                          onPause={() => setIsPlaying(false)}
                        />
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 w-0 transition-all" />
                        </div>
                      </div>

                      {recordingTime > 0 && (
                        <span className="text-sm text-gray-500 flex-shrink-0">
                          {formatTime(recordingTime)}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Transcription Editor */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Edit3 className="h-4 w-4" />
                    Transcription (Editable)
                  </label>
                  <span className="text-xs text-gray-500">
                    {transcription.length} characters
                  </span>
                </div>
                <Textarea
                  value={transcription}
                  onChange={(e) => setTranscription(e.target.value)}
                  rows={6}
                  placeholder="Edit the transcription if needed..."
                  className="font-mono text-sm"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={handleReset}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Discard
                </Button>
                <Button onClick={handleSave} className="bg-green-600 hover:bg-green-700">
                  <Check className="h-4 w-4 mr-2" />
                  Save Voice Note
                </Button>
              </div>
            </div>
          )}

          {/* Existing Notes Display */}
          {existingNotes.length > 0 && step === 'choice' && (
            <div className="border-t pt-4">
              <h4 className="font-semibold text-sm mb-2">Existing Voice Notes ({existingNotes.length})</h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {existingNotes.map((note, idx) => (
                  <div key={note.id || idx} className="p-2 bg-gray-50 rounded text-sm">
                    <p className="text-gray-700">{note.transcription}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(note.created_at).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}

export default VoiceNoteRecorderV2;
